'use client';
import { useState, useEffect, useRef } from 'react';
import ContactButton from './ContactButton';
import WaSmartButton from './WaSmartButton';
import { cleanCity } from '@/lib/city';
import { fmtRating } from '@/lib/format';
import { avatarImg } from '@/lib/imgUrl';

type Props = {
  proArea?: import('@/lib/proCityGate').ProArea | null; // the message only names a city this pro serves (2026-09-28)
  proName: string;
  proPhone?: string;
  whatsappValid?: number | null;
  imgSrc: string;
  rating10: number;
  proId?: number;
  proGender?: string | null;
  categorySlug?: string;
  categoryName?: string;
  reviewCount?: number;
  city?: string | null;
  sentinelId?: string;
};

export default function ProStickyBar({ proName, proPhone, whatsappValid, imgSrc, rating10, proId, proGender, categorySlug, categoryName, reviewCount, city, sentinelId = 'hero-sentinel', proArea}: Props) {
  const locationStr = cleanCity(city);
  const [visible, setVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = document.getElementById(sentinelId);
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [sentinelId]);

  // Reserve room at the bottom of the page equal to the bar's own height (mobile only)
  // so the fixed bar never covers the footer — the page just gets a bit more scroll room.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => {
      const h = mq.matches ? barRef.current?.offsetHeight || 0 : 0;
      document.body.style.paddingBottom = h ? `${h}px` : '';
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (barRef.current) ro.observe(barRef.current);
    mq.addEventListener('change', apply);
    return () => {
      document.body.style.paddingBottom = '';
      ro.disconnect();
      mq.removeEventListener('change', apply);
    };
  }, []);

  return (
    <div data-ct2-hide
      ref={barRef}
      className="md:hidden fixed inset-x-0 z-40 transition-all duration-300 ease-in-out"
      style={{
        bottom: 0,
        paddingBottom: 'env(safe-area-inset-bottom)',
        transform: visible ? 'translateY(0)' : 'translateY(110%)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}>
      <div className="flex items-center gap-2.5 px-3 py-2 relative"
        style={{
          background: 'var(--sk-bar, rgba(255,255,255,.97))',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid var(--sk-line, #e2e8f0)',
          boxShadow: '0 -4px 20px rgba(13,71,161,.12)',
        }}>
        <img {...avatarImg(imgSrc, 54, { kind: 'pro', ctx: { category: categoryName, proName: proName } })} width={54} height={54} loading="lazy" decoding="async"
          className="rounded-full object-cover flex-shrink-0"
          style={{ width: 54, height: 54, border: '2.5px solid #e0e7ff' }} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-black leading-tight truncate" style={{ color: 'var(--sk-head, #0f172a)' }}>{proName}</span>
            <span className="flex items-center gap-1 flex-shrink-0">
              <span style={{ color: '#f59e0b', fontSize: 17, lineHeight: 1 }}>★</span>
              <span className="text-base font-black" style={{ color: 'var(--sk-link, #0d47a1)' }}>{fmtRating(rating10)}</span>
              {typeof reviewCount === 'number' && <span className="text-xs" style={{ color: 'var(--sk-muted, #94a3b8)' }}>({reviewCount})</span>}
            </span>
          </div>
          {/* Category + location — each on its own line (was one shared truncated line with
              " · " between them, which regularly clipped the location off the end). */}
          {categoryName && (
            <div className="text-xs truncate mt-0.5" style={{ color: 'var(--sk-text, #64748b)' }}>{categoryName}</div>
          )}
          {locationStr && (
            <div className="text-xs truncate mt-0.5" style={{ color: 'var(--sk-text, #64748b)' }}>{locationStr} והסביבה</div>
          )}
        </div>

        <div className="flex gap-1.5 flex-shrink-0">
          {proPhone && <WaSmartButton proArea={proArea ?? null} proName={proName} proFirstName={proName} proPhone={proPhone} whatsappValid={whatsappValid} proId={proId} proGender={proGender} categorySlug={categorySlug} categoryName={categoryName} compact />}
          <ContactButton proName={proName} proFirstName={proName} proPhone={proPhone} proId={proId} proGender={proGender} categorySlug={categorySlug} categoryName={categoryName}
            label="התקשר"
            className="flex items-center gap-1 py-2 px-3 rounded-xl text-xs font-bold text-white flex-shrink-0 whitespace-nowrap" />
        </div>
      </div>
    </div>
  );
}
